import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Skeleton,
  Grid,
  Card,
  Divider,
} from "@mui/material";
import {
  Business,
  LocationOn,
  GridView,
  ViewList,
  Add,
  AccountTree,
  BarChart,
} from "@mui/icons-material";
import { Client, Organization } from "./types";
import { LocationModel } from "object-models/location-model";
import { useMsal } from "@azure/msal-react";
import { _getUserRole } from "middlewares/ManageUsers/manageusers";
import { _getClientHierarchyData } from "middlewares/ClientApi/client";
import FormDialog from "components/Dialog/modal-popup";
import AddClient from "pages/Client/ManageClient/AddClient";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import SnackBar from "components/SnackBar";
import AddOrganization from "pages/Client/Organization/AddOrganization";
import CreateUpdateLocation from "pages/CreateUpdateLocation";
import ClientStatistics from "./ClientStatistics";
import OrganizationCards from "./OrganizationCards";
import LocationCards from "./LocationCards";
import ClientCards from "./ClientCards";
import ClientListView from "./ClientListView";
import LocationListView from "./LocationListView";
import OrganizationLocationFilter from "./OrganizationLocationFilter";
import PortfolioView from "pages/PortfolioView/PortfolioView";
import { useAppState } from "context/appState.context";
import { useBreadcrumb } from "context/breadcrumb.context";
import { useLocation, useNavigate } from "react-router-dom";
import { UserRoles } from "constants/constant";

// Helper for statistics calculation
type ViewLevel = "client" | "organization" | "location";
type ViewMode = "grid" | "list";

function getStatistics(viewLevel: ViewLevel, clients: Client[], currentClient: Client | null, selectedOrganization: Organization | null) {
  function calculateLocationEmissions(location: any) {
    return (
      (location.scope1GHGEmission ?? 0) +
      (location.scope2GHGEmission ?? 0) +
      (location.scope3GHGEmission ?? 0)
    );
  }
  function calculateOrgEmissions(org: Organization) {
    if (!org.locations) return 0;
    return org.locations.reduce(
      (sum, loc) => sum + calculateLocationEmissions(loc),
      0,
    );
  }
  function getOrgLocationCount(org: Organization) {
    return org.locations?.length ?? 0;
  }
  if (viewLevel === "client") {
    const totalOrgs = clients.reduce(
      (sum, client) => sum + (client.organizations?.length ?? 0),
      0,
    );
    const totalLocs = clients.reduce((sum, client) => {
      const clientLocs =
        client.organizations?.reduce(
          (orgSum, org) => orgSum + getOrgLocationCount(org),
          0,
        ) ?? 0;
      return sum + clientLocs;
    }, 0);
    const totalEmissions = clients.reduce((sum, client) => {
      const clientEmissions =
        client.organizations?.reduce(
          (orgSum, org) => orgSum + calculateOrgEmissions(org),
          0,
        ) ?? 0;
      return sum + clientEmissions;
    }, 0);
    return {
      totalEmissions: totalEmissions,
      organizations: totalOrgs,
      locationsCount: totalLocs,
      targetCoverage: 78,
    };
  } else if (viewLevel === "organization" && currentClient) {
    const totalEmissions =
      currentClient.organizations?.reduce(
        (sum, org) => sum + calculateOrgEmissions(org),
        0,
      ) ?? 0;
    const totalLocs =
      currentClient.organizations?.reduce(
        (sum, org) => sum + getOrgLocationCount(org),
        0,
      ) ?? 0;
    return {
      totalEmissions: totalEmissions,
      organizations: currentClient.organizations?.length ?? 0,
      locationsCount: totalLocs,
      targetCoverage: 78,
    };
  } else if (viewLevel === "location" && selectedOrganization) {
    const totalEmissions =
      selectedOrganization.locations?.reduce(
        (sum, loc) => sum + calculateLocationEmissions(loc),
        0,
      ) ?? 0;
    return {
      totalEmissions: totalEmissions,
      organizations: 1,
      locationsCount: selectedOrganization.locations?.length ?? 0,
      targetCoverage: selectedOrganization.targetCoverage ?? 0,
    };
  }
  return {
    totalEmissions: 0,
    organizations: 0,
    locationsCount: 0,
    targetCoverage: 0,
  };
}

function getFilteredOrganizations(
  currentClient: Client | null,
  selectedOrgs: number[],
  selectedLocs: number[],
) {
  if (!currentClient?.organizations) return [];

  // If no filters are applied, return all organizations
  if (selectedOrgs.length === 0 && selectedLocs.length === 0) {
    return currentClient.organizations;
  }

  // Show organizations that are either:
  // 1. Directly selected in selectedOrgs
  // 2. Contain any selected locations
  const orgsWithSelectedLocations = currentClient.organizations.filter(
    (org) => org.locations?.some((loc) => selectedLocs.includes(loc.locationId)),
  );

  const directlySelectedOrgs = currentClient.organizations.filter((org) =>
    selectedOrgs.includes(org.orgId),
  );

  // Combine both sets of organizations (remove duplicates)
  const uniqueOrgIds = new Set([
    ...orgsWithSelectedLocations.map((org) => org.orgId),
    ...directlySelectedOrgs.map((org) => org.orgId),
  ]);

  if (uniqueOrgIds.size === 0) {
    return currentClient.organizations;
  }

  // Return filtered organizations with only selected locations shown
  return currentClient.organizations
    .filter((org) => uniqueOrgIds.has(org.orgId))
    .map((org) => {
      // If this org has selected locations, show only those locations
      if (
        selectedLocs.length > 0 &&
        org.locations?.some((loc) => selectedLocs.includes(loc.locationId))
      ) {
        return {
          ...org,
          locations:
            org.locations?.filter((loc) =>
              selectedLocs.includes(loc.locationId),
            ) || [],
        };
      }
      // Otherwise, show all locations of the organization
      return org;
    });
}

function getFilteredLocations(
  currentClient: Client | null,
  selectedOrganization: Organization | null,
  selectedLocs: number[],
) {
  if (!selectedOrganization || !currentClient) return [];

  // Always get the full organization from currentClient to ensure all locations are available
  const fullOrganization = currentClient.organizations?.find(
    (org) => org.orgId === selectedOrganization.orgId,
  );

  if (!fullOrganization?.locations) return [];

  // If no location filters, return all locations
  if (selectedLocs.length === 0) {
    return fullOrganization.locations;
  }

  // Return only selected locations
  return fullOrganization.locations.filter((loc) =>
    selectedLocs.includes(loc.locationId),
  );
}

function getPortfolioTotalsFromLocations(locations: LocationModel[]) {
  const scope1Total = locations.reduce(
    (sum, loc) => sum + (loc.scope1GHGEmission ?? 0),
    0,
  );
  const scope2Total = locations.reduce(
    (sum, loc) => sum + (loc.scope2GHGEmission ?? 0),
    0,
  );
  const scope3Total = locations.reduce(
    (sum, loc) => sum + (loc.scope3GHGEmission ?? 0),
    0,
  );

  return {
    totalEmissions: scope1Total + scope2Total + scope3Total,
    scope1Total,
    scope2Total,
    scope3Total,
  };
}

function getClientPortfolioData(organizations: Organization[]) {
  const allLocations = organizations.reduce<LocationModel[]>((acc, org) => {
    if (org.locations?.length) {
      acc.push(...org.locations);
    }
    return acc;
  }, []);

  return {
    locationCount: allLocations.length,
    ...getPortfolioTotalsFromLocations(allLocations),
  };
}

const ClientDetailView: React.FC = () => {
  const { accounts } = useMsal();
  const { userRole, setUserRole, setClientId } = useAppState();
  const showClientPortfolioButton = true;
  const showOrganizationPortfolioButton = true;
  const toolbarControlSx = {
    textTransform: "none",
    fontSize: "0.76rem",
    fontWeight: 500,
    py: 0.45,
    px: 1.4,
    minHeight: 32,
    borderRadius: "6px",
  };
  const [clients, setClients] = useState<Client[]>([]);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  
  const [viewLevel, setViewLevel] = useState<ViewLevel>("client");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [listViewExpandedClientId, setListViewExpandedClientId] = useState<number | null>(null);
  const [listViewExpandedOrgId, setListViewExpandedOrgId] = useState<number | null>(null);
  const [selectedOrgs, setSelectedOrgs] = useState<number[]>([]);
  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationDialogIsEdit, setLocationDialogIsEdit] = useState(false);
  const [clientDialogInitialTab, setClientDialogInitialTab] = useState(0);
  const [orgDialogInitialTab, setOrgDialogInitialTab] = useState(0);

  const [dialogTitle, setDialogTitle] = useState("");
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const hasFetchedRef = useRef(false);
  const clientsRef = useRef<Client[]>([]);
  const [selectedClientData, setSelectedClientData] = useState<Client | null>(null);
  const [showOrgPortfolioView, setShowOrgPortfolioView] = useState(false);
  const [portfolioTitle, setPortfolioTitle] = useState("Organization Portfolio");

  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const { setBreadcrumbs } = useBreadcrumb();
  const location = useLocation();
  const navigate = useNavigate();

  // Sync breadcrumb context whenever viewLevel, currentClient, or selectedOrganization changes
  useEffect(() => {
    const items: { label: string; onClick?: () => void; isActive?: boolean }[] = [];

    if (currentClient) {
      items.push({
        label: currentClient.clientName,
        isActive: viewLevel === "organization" || (viewLevel === "client" && !selectedOrganization),
        onClick:
          viewLevel === "location"
            ? () => {
                if (viewMode === "list") {
                  navigate("/Clients", {
                    state: {
                      viewLevel: "client",
                      clientId: currentClient.clientId,
                      orgId: selectedOrganization?.orgId,
                      viewMode,
                      expandedClientId: currentClient.clientId,
                    },
                  });
                } else {
                  setSelectedOrganization(null);
                  setViewLevel("organization");
                  navigate("/Organizations", {
                    state: {
                      viewLevel: "organization",
                      clientId: currentClient.clientId,
                      viewMode,
                    },
                  });
                }
              }
            : viewLevel === "client" && selectedOrganization
            ? () => {
                setSelectedOrganization(null);
              }
            : undefined,
      });
    }

    if (selectedOrganization) {
      items.push({
        label: selectedOrganization.orgName,
        isActive: true,
      });
    }

    setBreadcrumbs(items);

    // Store client/org names for location-detail pages
    if (currentClient) {
      localStorage.setItem("breadcrumb_clientName", currentClient.clientName);
      localStorage.setItem("breadcrumb_clientId", String(currentClient.clientId));
    }
    if (selectedOrganization) {
      localStorage.setItem("breadcrumb_orgName", selectedOrganization.orgName);
      localStorage.setItem("breadcrumb_orgId", String(selectedOrganization.orgId));
    }

    return () => setBreadcrumbs([]);
  }, [viewLevel, currentClient, selectedOrganization, setBreadcrumbs, navigate, viewMode]);

  useEffect(() => {
    localStorage.setItem("clientDetailViewMode", viewMode);
  }, [viewMode]);

 useEffect(() => {
    sessionStorage.removeItem("userRole");
    if (accounts.length > 0) {
      const email = accounts[0].idTokenClaims?.email as string;
      if (email?.length) {
        getUserRole(email);
      }
    }
  }, [accounts]);

  const getUserRole = async (email: string) => {
    try {
      const res = await _getUserRole(email);
      if (res) {
        setUserRole(res);
        sessionStorage.setItem("userRole", res);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const res = await _getClientHierarchyData();
      if (res) {
        setClients(res);

        // Update currentClient if it exists
        if (currentClient) {
          const updatedCurrentClient = res.find(c => c.clientId === currentClient.clientId);
          if (updatedCurrentClient) {
            setCurrentClient(updatedCurrentClient);
          }
        }

        // Auto-navigate to organization view if only one client exists
        if (res.length === 1 && !currentClient) {
          setCurrentClient(res[0]);
          setClientId(res[0].clientId);
          setViewLevel("organization");
          navigate("/Organizations", {
            replace: true,
            state: { viewLevel: "organization", clientId: res[0].clientId, _applied: true },
          });
        }
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleListViewClientExpand = (client: Client | null) => {
    setCurrentClient(client);
    setSelectedOrganization(null);
    setListViewExpandedClientId(client?.clientId ?? null);
    setListViewExpandedOrgId(null);
  };

  const handleListViewOrgExpand = (org: Organization | null, client: Client | null) => {
    setCurrentClient(client);
    setSelectedOrganization(org);
    setListViewExpandedClientId(client?.clientId ?? null);
    setListViewExpandedOrgId(org?.orgId ?? null);
  };

  const handleListViewOrgDeleted = (orgId: number) => {
    setClients((prev) =>
      prev.map((client) => ({
        ...client,
        organizations: client.organizations?.filter((org) => org.orgId !== orgId) ?? [],
      })),
    );

    if (selectedOrganization?.orgId === orgId) {
      setSelectedOrganization(null);
    }

    if (listViewExpandedOrgId === orgId) {
      setListViewExpandedOrgId(null);
    }
  };

  const handleListViewLocationDeleted = (locationId: number) => {
    setClients((prev) =>
      prev.map((client) => ({
        ...client,
        organizations: client.organizations?.map((org) => ({
          ...org,
          locations: org.locations?.filter((loc) => loc.locationId !== locationId) ?? [],
        })) ?? [],
      })),
    );
  };

  const handleListViewLocationEdit = (location: LocationModel) => {
    localStorage.setItem("LocationId", String(location.locationId));
    setDialogTitle("Edit Location Details");
    setLocationDialogIsEdit(true);
    setLocationDialogOpen(true);
  };

  const handleListViewLocAdd = (org: Organization) => {
    localStorage.setItem("orgId", String(org.orgId));
    let clientInOperation = clients?.find(
      (c) => c.clientId === (org.clientId ?? org.clientID)
    );
    setCurrentClient(clientInOperation || null);
    const currentOrganization = clientInOperation?.organizations?.find(
      (o) => o.orgId === org.orgId,
    );
    if (!currentOrganization) return;
    setSelectedOrganization(currentOrganization);
    setDialogTitle("Add Location");
    setLocationDialogIsEdit(false);
    setLocationDialogOpen(true);
  }

  const handleClientClick = (client: Client) => {
    setCurrentClient(client);
    setClientId(client.clientId);
    setViewLevel("organization");
    setShowOrgPortfolioView(false);
    // Reset filters when changing client
    setSelectedOrgs([]);
    setSelectedLocs([]);
    navigate("/Organizations", {
      state: { viewLevel: "organization", clientId: client.clientId, _applied: true },
    });
  };

  const handleEditClient = (client: Client) => {
    setSelectedClientData(client);
    setDialogTitle("Edit Client");
    setClientDialogInitialTab(0);
    setDialogOpen(true);
  };

  const handleSetTargetClient = (client: Client) => {
    setSelectedClientData(client);
    setDialogTitle("Edit Client");
    setClientDialogInitialTab(2);
    setDialogOpen(true);
  };

  const handleClientPortfolioClick = (client: Client) => {
    setCurrentClient(client);
    setClientId(client.clientId);
    setSelectedOrganization(null);
    // Reset filters so client portfolio always reflects the full selected client
    setSelectedOrgs([]);
    setSelectedLocs([]);
    setPortfolioTitle("Client Portfolio");
    setShowOrgPortfolioView(true);
    setViewLevel("organization");
    navigate("/Organizations", {
      state: { viewLevel: "organization", clientId: client.clientId, _applied: true },
    });
  };

  const handleOrganizationClick = (organization: Organization) => {
    // Always get the full organization data from currentClient, not the filtered version
    const fullOrganization = currentClient?.organizations?.find(
      (org) => org.orgId === organization.orgId,
    );
    const orgToSet = fullOrganization || organization;
    setSelectedOrganization(orgToSet);
    setViewLevel("location");
    setShowOrgPortfolioView(false);
    navigate("/Locations", {
      state: { viewLevel: "location", clientId: currentClient?.clientId, orgId: orgToSet.orgId, _applied: true },
    });
  };

  const handleOrganizationPortfolioClick = (client: Client, organization: Organization) => {
    const fullOrganization = client.organizations?.find(
      (org) => org.orgId === organization.orgId,
    ) || organization;

    setCurrentClient(client);
    setClientId(client.clientId);
    setSelectedOrganization(fullOrganization);
    setSelectedOrgs([fullOrganization.orgId]);
    setSelectedLocs([]);
    setPortfolioTitle("Organization Portfolio");
    setShowOrgPortfolioView(true);
    setViewLevel("location");
    navigate("/Locations", {
      state: {
        viewLevel: "location",
        clientId: client.clientId,
        orgId: fullOrganization.orgId,
        _applied: true,
      },
    });
  };

  const handleListViewOrgAdd = (client: Client) => {
    setCurrentClient(client);
    setDialogTitle("Add Organization");
    setOrgDialogInitialTab(0);
    setOrgDialogOpen(true);
  };

  const handleEditOrganization= (organization: Organization) => {
    let clientInOperation = clients?.find(
      (c) => c.clientId === (organization.clientId ?? organization.clientID)
    );
    setCurrentClient(clientInOperation || null);
    const currentOrganization = clientInOperation?.organizations?.find(
      (org) => org.orgId === organization.orgId,
    );
    if (!currentOrganization) return;
    setDialogTitle("Edit Organization");
    setSelectedOrganization(currentOrganization);
    setOrgDialogInitialTab(0);
    setOrgDialogOpen(true);
  };

  const handleSetTargetOrganization = (organization: Organization) => {
    let clientInOperation = clients?.find(
      (c) => c.clientId === (organization.clientId ?? organization.clientID)
    );
    setCurrentClient(clientInOperation || null);
    const currentOrganization = clientInOperation?.organizations?.find(
      (org) => org.orgId === organization.orgId,
    );
    if (!currentOrganization) return;
    setDialogTitle("Edit Organization");
    setSelectedOrganization(currentOrganization);
    setOrgDialogInitialTab(1);
    setOrgDialogOpen(true);
  };

  // Filter functions
  const handleOrganizationFilterChange = (selectedOrgIds: number[]) => {
    setSelectedOrgs(selectedOrgIds);
  };

  const handleLocationFilterChange = (selectedLocationIds: number[]) => {
    setSelectedLocs(selectedLocationIds);
  };

  const filteredOrganizations = getFilteredOrganizations(
    currentClient,
    selectedOrgs,
    selectedLocs,
  );
  const filteredLocations = getFilteredLocations(
    currentClient,
    selectedOrganization,
    selectedLocs,
  );
  const clientPortfolioData = getClientPortfolioData(filteredOrganizations);
  const locationPortfolioData = getPortfolioTotalsFromLocations(filteredLocations);

  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  const fetchClientDetailsSilent = async () => {
    try {
      const res = await _getClientHierarchyData();
      if (res) {
        setClients(res);
        if (currentClient) {
          const updated = res.find((c) => c.clientId === currentClient.clientId);
          if (updated) setCurrentClient(updated);
        }
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const handleClientUpdated = (savedClient: Client) => {
    setClients((prev) =>
      prev.map((c) =>
        c.clientId === savedClient.clientId
          ? { ...c, ...savedClient, organizations: c.organizations }
          : c
      )
    );
    if (currentClient?.clientId === savedClient.clientId) {
      setCurrentClient((prev) =>
        prev ? { ...prev, ...savedClient, organizations: prev.organizations } : null
      );
    }
  };

  const handleClientCreated = (_savedClient: Client) => {
    fetchClientDetailsSilent();
  };

  const handleOrganizationSaved = (savedOrg: Organization, isEdit: boolean) => {
    if (!currentClient) return;
    if (isEdit) {
      const updatedOrgs =
        currentClient.organizations?.map((org) =>
          org.orgId === savedOrg.orgId
            ? { ...org, ...savedOrg, locations: org.locations }
            : org
        ) ?? [];
      const updatedClient = { ...currentClient, organizations: updatedOrgs };
      setCurrentClient(updatedClient);
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === currentClient.clientId ? updatedClient : c
        )
      );
    } else {
      fetchClientDetailsSilent();
    }
  };

  const handleLocationCreated = (location: LocationModel) => {
    if (!selectedOrganization || !currentClient) return;
    const updatedOrg = {
      ...selectedOrganization,
      locations: [...(selectedOrganization.locations ?? []), location],
    };
    setSelectedOrganization(updatedOrg);
    const updatedClient = {
      ...currentClient,
      organizations: currentClient.organizations?.map((org) =>
        org.orgId === selectedOrganization.orgId ? updatedOrg : org
      ),
    };
    setCurrentClient(updatedClient);
    setClients((prev) =>
      prev.map((c) =>
        c.clientId === currentClient.clientId ? updatedClient : c
      )
    );
  };

  const hideDialogHandler = () => {
    setDialogOpen(false);
    setSelectedClientData(null);
  };

  const hideOrgDialogHandler = () => {
    setOrgDialogOpen(false);
    setSelectedOrganization(null);
  };

  const hideLocationDialogHandler = () => {
    // Edits aren't reflected in local state, so refetch to show the saved changes
    if (locationDialogIsEdit) {
      fetchClientDetailsSilent();
    }
    setLocationDialogOpen(false);
    setLocationDialogIsEdit(false);
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchClientDetails();
  }, []);

  // Keep clientsRef in sync so the popstate listener always has fresh data
  useEffect(() => {
    clientsRef.current = clients;
  }, [clients]);

  // Set initial URL and handle browser back/forward navigation
  useEffect(() => {
    // Only stamp /Clients on a fresh load with no incoming navigation state.
    // When navigating back from LocationDetails via breadcrumb, location.state
    // carries the target viewLevel — the navState effect handles the URL then.
    if (!location.state) {
      navigate("/Clients", { replace: true, state: { viewLevel: "client", _applied: true } });
    }

    const handlePopState = (event: PopStateEvent) => {
      // React Router v6 stores user state under event.state.usr; fall back to raw state for compatibility
      const raw = event.state as { usr?: unknown; [k: string]: unknown } | null;
      const state = (raw?.usr ?? raw) as {
        viewLevel?: ViewLevel;
        clientId?: number;
        orgId?: number;
        viewMode?: ViewMode;
        expandedClientId?: number;
        expandedOrgId?: number;
      } | null;

      if (state?.viewMode) {
        setViewMode(state.viewMode);
      }

      if (state?.viewMode === "list" && state.clientId != null) {
        const client = clientsRef.current.find((c) => c.clientId === state.clientId) || null;
        const org = state.orgId != null
          ? client?.organizations?.find((o) => o.orgId === state.orgId) || null
          : null;
        setCurrentClient(client);
        setSelectedOrganization(org);
        setListViewExpandedClientId(state.expandedClientId ?? client?.clientId ?? null);
        setListViewExpandedOrgId(state.expandedOrgId ?? org?.orgId ?? null);
        setViewLevel("client");
        setShowOrgPortfolioView(false);
        return;
      }

      if (!state || state.viewLevel === "client") {
        setViewLevel("client");
        setCurrentClient(null);
        setSelectedOrganization(null);
        setShowOrgPortfolioView(false);
      } else if (state.viewLevel === "organization" && state.clientId != null) {
        const client = clientsRef.current.find((c) => c.clientId === state.clientId) || null;
        setCurrentClient(client);
        setSelectedOrganization(null);
        setViewLevel("organization");
        setShowOrgPortfolioView(false);
      } else if (
        state.viewLevel === "location" &&
        state.clientId != null &&
        state.orgId != null
      ) {
        const client = clientsRef.current.find((c) => c.clientId === state.clientId) || null;
        const org = client?.organizations?.find((o) => o.orgId === state.orgId) || null;
        setCurrentClient(client);
        setSelectedOrganization(org);
        setViewLevel("location");
        setShowOrgPortfolioView(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Handle navigation state from breadcrumb clicks (e.g., from LocationLayout).
  // _applied prevents this effect from re-running after we stamp the URL ourselves.
  type NavState = {
    clientId?: number;
    orgId?: number;
    viewLevel?: ViewLevel;
    viewMode?: ViewMode;
    expandedClientId?: number;
    expandedOrgId?: number;
    _applied?: boolean;
  };

  useEffect(() => {
    const navigateToOrgLocation = (client: Client, navState: NavState, orgId: number) => {
      const org = client.organizations?.find((o) => o.orgId === orgId);
      if (org) {
        setSelectedOrganization(org);
        setViewLevel("location");
        setShowOrgPortfolioView(false);
        navigate("/Locations", { replace: true, state: { ...navState, _applied: true } });
      } else {
        setViewLevel("organization");
        setShowOrgPortfolioView(false);
        navigate("/Organizations", { replace: true, state: { ...navState, _applied: true } });
      }
    };

    const navigateToClient = (client: Client, navState: NavState) => {
      setCurrentClient(client);
      setClientId(client.clientId);
      if (navState.viewLevel === "location" && navState.orgId) {
        navigateToOrgLocation(client, navState, navState.orgId);
      } else {
        const resolvedLevel: ViewLevel =
          navState.viewLevel === "location" ? "organization" : (navState.viewLevel ?? "organization");
        setSelectedOrganization(null);
        setViewLevel(resolvedLevel);
        setShowOrgPortfolioView(false);
        const resolvedUrl =
          resolvedLevel === "organization" ? "/Organizations" : "/Clients";
        navigate(resolvedUrl, { replace: true, state: { ...navState, _applied: true } });
      }
    };

    const navState = location.state as NavState | null;

    // Skip state that was set by this component (already applied) to prevent
    // re-triggering on every setClients call (silent fetches, deletes, etc.).
    if (!navState || navState._applied) return;

    if (navState.viewMode) {
      setViewMode(navState.viewMode);
    }

    if (navState.viewMode === "list" && navState.clientId) {
      const client = clients.find((c) => c.clientId === navState.clientId);
      if (!client) return;

      const org = navState.orgId != null
        ? client.organizations?.find((o) => o.orgId === navState.orgId) || null
        : null;

      setCurrentClient(client);
      setClientId(client.clientId);
      setSelectedOrganization(org);
      setListViewExpandedClientId(navState.expandedClientId ?? client.clientId);
      setListViewExpandedOrgId(navState.expandedOrgId ?? org?.orgId ?? null);
      setViewLevel("client");
      setShowOrgPortfolioView(false);
      navigate("/Clients", {
        replace: true,
        state: {
          ...navState,
          viewLevel: "client",
          expandedClientId: navState.expandedClientId ?? client.clientId,
          expandedOrgId: navState.expandedOrgId ?? org?.orgId,
          _applied: true,
        },
      });
      return;
    }

    if (navState.viewLevel === "client") {
      setCurrentClient(null);
      setSelectedOrganization(null);
      setViewLevel("client");
      setShowOrgPortfolioView(false);
      navigate("/Clients", { replace: true, state: { ...navState, _applied: true } });
      return;
    }

    if (!navState.clientId || clients.length === 0) return;

    const client = clients.find((c) => c.clientId === navState.clientId);
    if (client) {
      navigateToClient(client, navState);
    }
  }, [clients, location.state]);

  const handleOrganizationDeleted = (orgId: number) => {
    if (!currentClient) return;

    const updatedOrganizations =
      currentClient.organizations?.filter((org) => org.orgId !== orgId) || [];

    const updatedClient = {
      ...currentClient,
      organizations: updatedOrganizations,
    };

    setCurrentClient(updatedClient);
    
    setClients((prev) =>
      prev.map((client) =>
        client.clientId === currentClient.clientId ? updatedClient : client,
      ),
    );
  };

  const handleLocationDeleted = (locationId: number) => {
  if (!selectedOrganization || !currentClient) return;

  // Remove from the selected organization
  const updatedLocations = selectedOrganization.locations?.filter(loc => loc.locationId !== locationId) || [];

  const updatedOrganization = {
    ...selectedOrganization,
    locations: updatedLocations,
  };

  setSelectedOrganization(updatedOrganization);

  // Also update inside currentClient.organizations
  const updatedOrganizations = currentClient.organizations?.map(org => {
    if (org.orgId === selectedOrganization.orgId) {
      return {
        ...org,
        locations: updatedLocations,
      };
    }
    return org;
  });

  const updatedClient = {
    ...currentClient,
    organizations: updatedOrganizations,
  };

  setCurrentClient(updatedClient);

  // If you use locations in other state, update there as well
};

  const handleClientDeleted = (clientId: number) => {
    setClients((prev) =>
      prev.filter((client) => client.clientId !== clientId)
    );

    if (listViewExpandedClientId === clientId) {
      setListViewExpandedClientId(null);
      setListViewExpandedOrgId(null);
    }
    
    // Reset view if the deleted client was the current one
    if (currentClient?.clientId === clientId) {
      setCurrentClient(null);
      setViewLevel("client");
    }
  };

  const handlePortfolioBack = () => {
    setShowOrgPortfolioView(false);
    if (viewMode !== "list" || !currentClient) return;

    const expandedOrgId = selectedOrganization?.orgId ?? listViewExpandedOrgId;
    setViewLevel("client");
    setListViewExpandedClientId(currentClient.clientId);
    setListViewExpandedOrgId(expandedOrgId);
    navigate("/Clients", {
      state: {
        viewLevel: "client",
        viewMode,
        clientId: currentClient.clientId,
        orgId: selectedOrganization?.orgId,
        expandedClientId: currentClient.clientId,
        expandedOrgId,
        _applied: true,
      },
    });
  };

  const renderOrganizationContent = () => {
    if (!currentClient) return null;

    if (showOrgPortfolioView) {
      return (
        <PortfolioView
          onBack={handlePortfolioBack}
          clientId={currentClient.clientId}
          IsClientView={true}
          portfolioTitle={portfolioTitle}
          userRole={userRole}
          clientName={currentClient.clientName}
          locationCount={clientPortfolioData.locationCount}
          totalEmissions={clientPortfolioData.totalEmissions}
          scope1Total={clientPortfolioData.scope1Total}
          scope2Total={clientPortfolioData.scope2Total}
          scope3Total={clientPortfolioData.scope3Total}
          organizationsList={
            currentClient.organizations?.map((org) => ({
              orgId: org.orgId,
              orgName: org.orgName,
              locationIds:
                org.locations?.map((loc) => loc.locationId) || [],
            })) || []
          }
          locationsList={
            currentClient.organizations?.flatMap((org) =>
              (org.locations ?? []).map((loc) => ({
                locationId: loc.locationId,
                locationName: loc.locationName?.trim() ? loc.locationName : `Location ${loc.locationId}`,
              }))
            ) || []
          }
        />
      );
    }

    if (currentClient.organizations && currentClient.organizations.length > 0) {
      return (
        <OrganizationCards
          organizations={filteredOrganizations}
          onOrganizationClick={handleOrganizationClick}
          onEditOrganizationClick={handleEditOrganization}
          onOrganizationDeleted={handleOrganizationDeleted}
          onSetTargetClick={handleSetTargetOrganization}
        />
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          textAlign: "center",
        }}
      >
        <Business
          sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Organizations Found
        </Typography>
        <Typography variant="body2" color="text.disabled">
          This client doesn't have any organizations yet. Add one
          using the "Add Organization" button above.
        </Typography>
      </Box>
    );
  };

  const renderLocationContent = () => {
    if (!selectedOrganization) return null;

    if (showOrgPortfolioView) {
      return (
        <PortfolioView
          onBack={handlePortfolioBack}
          clientId={currentClient?.clientId}
          orgId={selectedOrganization.orgId}
          IsClientView={false}
          portfolioTitle={portfolioTitle}
          userRole={userRole}
          clientName={currentClient?.clientName}
          organizationName={selectedOrganization.orgName}
          locationCount={filteredLocations.length}
          totalEmissions={locationPortfolioData.totalEmissions}
          scope1Total={locationPortfolioData.scope1Total}
          scope2Total={locationPortfolioData.scope2Total}
          scope3Total={locationPortfolioData.scope3Total}
          locationsList={
            selectedOrganization.locations?.map((loc) => ({
              locationId: loc.locationId,
              locationName: loc.locationName ?? `Location ${loc.locationId}`,
            })) || []
          }
        />
      );
    }

    if (selectedOrganization.locations && selectedOrganization.locations.length > 0) {
      if (viewMode === "grid") {
        return (
          <LocationCards
            locations={filteredLocations}
            onLocationDeleted={handleLocationDeleted}
            onLocationUpdated={fetchClientDetailsSilent}
          />
        );
      }

      return (
        <LocationListView
          locations={filteredLocations}
          onLocationDeleted={handleLocationDeleted}
          onLocationUpdated={fetchClientDetailsSilent}
        />
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          textAlign: "center",
        }}
      >
        <LocationOn
          sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Locations Found
        </Typography>
        <Typography variant="body2" color="text.disabled">
          This organization doesn't have any locations yet. Add one
          using the "Add Location" button above.
        </Typography>
      </Box>
    );
  };

  

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          width: "100%",
          bgcolor: "#fafafa",
        }}
      >
          {/* Statistics Skeleton */}
          <Box sx={{ bgcolor: "white", p: 2, mb: 3, boxShadow: 0 }}>
            <Grid container spacing={3}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Skeleton variant="rectangular" height={20} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 3, pt: 0 }}>
            {/* Breadcrumb Skeleton */}
            <Box
              sx={{
                mb: 2,
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Skeleton variant="text" width={200} height={30} />
              <Skeleton variant="rectangular" width={100} height={30} />
            </Box>

            {/* Cards Skeleton */}
            <Grid container spacing={2}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
                <Grid item xs={12} sm={6} md={2.4} key={item}>
                  <Card sx={{ height: 140 }}>
                    <Skeleton variant="rectangular" height={70} />
                    <Box sx={{ p: 1.25 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          bgcolor: "#fafafa",
        }}
      >
        <ClientStatistics currentClient={getStatistics(viewLevel, clients, currentClient, selectedOrganization)} />

        <Box sx={{ flex: 1, overflow: "auto", p: 3, pt: 0 }}>
          <Box
            sx={{
              mb: 2,
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >

            {/* View Toggle Icons and Add Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "auto" }}>
              {!showOrgPortfolioView && (
                <>
              {/* Filter Dropdown - Show at organization and location levels */}
              {(viewLevel === "organization" || viewLevel === "location") &&
                currentClient && (
                  <OrganizationLocationFilter
                    organizations={currentClient.organizations || []}
                    selectedOrganizations={selectedOrgs}
                    selectedLocations={selectedLocs}
                    onOrganizationChange={handleOrganizationFilterChange}
                    onLocationChange={handleLocationFilterChange}
                    placeholderText={
                      viewLevel === "location"
                        ? "Filter Locations"
                        : "Filter Organizations"
                    }
                    controlHeight={34}
                  />
                )}

              {showClientPortfolioButton && viewLevel === "organization" && currentClient &&
                currentClient.organizations?.some((org) => (org.locations?.length ?? 0) > 0) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BarChart sx={{ fontSize: 16 }} />}
                  sx={{
                    ...toolbarControlSx,
                    borderColor: "#E57A1F",
                    color: "#E57A1F",
                    backgroundColor: "#fff7ef",
                    "&:hover": {
                      borderColor: "#D26A11",
                      backgroundColor: "#fff1e4",
                    },
                  }}
                  onClick={() => {
                    setPortfolioTitle("Client Portfolio");
                    setShowOrgPortfolioView(true);
                  }}
                >
                  Client Portfolio
                </Button>
              )}

              {showOrganizationPortfolioButton && viewLevel === "location" && (selectedOrganization?.locations?.length ?? 0) > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AccountTree sx={{ fontSize: 16 }} />}
                  sx={{
                    ...toolbarControlSx,
                    borderColor: "#E57A1F",
                    color: "#E57A1F",
                    backgroundColor: "#fff7ef",
                    "&:hover": {
                      borderColor: "#D26A11",
                      backgroundColor: "#fff1e4",
                    },
                  }}
                  onClick={() => {
                    setPortfolioTitle("Organization Portfolio");
                    setShowOrgPortfolioView(true);
                  }}
                >
                  Organization Portfolio
                </Button>
              )}

              {viewLevel === "client" &&
                (userRole === UserRoles.SuperAdmin || userRole === UserRoles.Admin) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  sx={{
                    ...toolbarControlSx,
                  }}
                  onClick={() => {
                    setClientDialogInitialTab(0);
                    setDialogOpen(true);
                    setDialogTitle("Add Client");
                  }}
                >
                  Add Client
                </Button>
              )}
              {viewLevel === "organization" && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  sx={{
                    ...toolbarControlSx,
                  }}
                  onClick={() => {
                    setOrgDialogOpen(true);
                    setDialogTitle("Add Organization");
                  }}
                >
                  Add Organization
                </Button>
              )}
              {viewLevel === "location" && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add sx={{ fontSize: 1 }} />}
                  sx={{
                    ...toolbarControlSx,
                    bgcolor: "#E57A1F",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#D26A11",
                      boxShadow: "none",
                    },
                  }}
                  onClick={() => {
                    if (selectedOrganization) {
                      localStorage.setItem('orgId', String(selectedOrganization.orgId));
                      localStorage.setItem('clientId', String(currentClient?.clientId));
                    }
                    setLocationDialogOpen(true);
                    setDialogTitle("Add Location");
                  }}
                >
                  Add Location
                </Button>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: "#f9fafb",
                  height: 32,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (viewMode !== "list") {
                      setViewMode("list");
                      setViewLevel("client");
                      setCurrentClient(null);
                      setSelectedOrganization(null);
                    }
                  }}
                  sx={{
                    color: viewMode === "list" ? "#E57A1F" : "#9ca3af",
                    borderRadius: 0,
                    backgroundColor: viewMode === "list" ? "#fff1e8" : "transparent",
                    "&:hover": { bgcolor: viewMode === "list" ? "#ffe6d1" : "#f3f4f6" },
                    height: 32,
                    width: 32,
                    p: 0,
                  }}
                >
                  <ViewList sx={{ fontSize: "1rem" }} />
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#d1d5db" }} />
                <IconButton
                  size="small"
                  onClick={() => {
                    if (viewMode !== "grid") {
                      setViewMode("grid");
                      setViewLevel("client");
                      setCurrentClient(null);
                      setSelectedOrganization(null);
                    }
                  }}
                  sx={{
                    color: viewMode === "grid" ? "#E57A1F" : "#9ca3af",
                    borderRadius: 0,
                    backgroundColor: viewMode === "grid" ? "#fff1e8" : "transparent",
                    "&:hover": { bgcolor: viewMode === "grid" ? "#ffe6d1" : "#f3f4f6" },
                    height: 32,
                    width: 32,
                    p: 0,
                  }}
                >
                  <GridView sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Client Level */}
          {viewLevel === "client" && (
            <Box sx={{ mb: 4 }}>
              {(() => {
                if (clients.length === 0) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 8,
                        textAlign: "center",
                      }}
                    >
                      <Business
                        sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                      />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Clients Found
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        Get started by adding your first client using the "Add
                        Client" button above.
                      </Typography>
                    </Box>
                  );
                }
                if (viewMode === "grid") {
                  return (
                    <ClientCards
                      clients={clients}
                      onClientClick={handleClientClick}
                      onEditClick={handleEditClient}
                      onClientDeleted={handleClientDeleted}
                      onSetTargetClick={handleSetTargetClient}
                    />
                  );
                }
                return (
                  <ClientListView
                    clients={clients}
                    expandedClientId={listViewExpandedClientId}
                    expandedOrgId={listViewExpandedOrgId}
                    onClientClick={handleClientClick}
                    onClientPortfolioClick={handleClientPortfolioClick}
                    onOrganizationPortfolioClick={handleOrganizationPortfolioClick}
                    onEditClick={handleEditClient}
                    onClientDeleted={handleClientDeleted}
                    onSetTargetClick={handleSetTargetClient}
                    onClientExpand={handleListViewClientExpand}
                    onOrgExpand={handleListViewOrgExpand}
                    onOrgEditClick={handleEditOrganization}
                    onOrgDeleted={handleListViewOrgDeleted}
                    onLocationEditClick={handleListViewLocationEdit}
                    onLocationDeleted={handleListViewLocationDeleted}
                    onOrgAddClick={handleListViewOrgAdd}
                    onLocationAddClick={handleListViewLocAdd}
                    onSetTargetOrgClick={handleSetTargetOrganization}
                  />
                );
              })()}
            </Box>
          )}

          {/* Organization Level */}
          {viewLevel === "organization" && currentClient && (
            <Box sx={{ mb: 4 }}>
              {renderOrganizationContent()}
            </Box>
          )}

          {/* Location Level */}
          {viewLevel === "location" && selectedOrganization && (
            <Box>
              {renderLocationContent()}
            </Box>
          )}
        </Box>
      </Box>
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          handleCloseSnackBar({ show: false, severity: "info", message: "" })
        }
      />
      <FormDialog
        dialogOpen={dialogOpen}
        hideDialogHandler={hideDialogHandler}
        dialogTitle={dialogTitle}
        isFullScreenAllowed={true}
        maxWidth="xl"
        height="500px"
        sx={{ "& .MuiDialog-paper": { width: "65%", maxWidth: "65%" } }}
        childComponent={
          <AddClient
            handleCloseSnackBar={handleCloseSnackBar}
            hideDialogHandler={() => {
              hideDialogHandler();
            }}
            rowData={selectedClientData}
            initialTab={clientDialogInitialTab}
            onClientUpdated={handleClientUpdated}
            onClientCreated={handleClientCreated}
          />
        }
      ></FormDialog>
      <FormDialog
        dialogOpen={orgDialogOpen}
        hideDialogHandler={hideOrgDialogHandler}
        dialogTitle={dialogTitle}
        isFullScreenAllowed={true}
        maxWidth="xl"
        sx={{ "& .MuiDialog-paper": { maxWidth: "65%" } }}
        height="500px"
        childComponent={
          <AddOrganization
            handleCloseSnackBar={handleCloseSnackBar}
            hideDialogHandler={() => {
              hideOrgDialogHandler();
            }}
            clientId={currentClient?.clientId}
            rowData={selectedOrganization}
            initialTab={orgDialogInitialTab}
            onOrganizationSaved={handleOrganizationSaved}
          />
        }
      ></FormDialog>
      <FormDialog
        dialogOpen={locationDialogOpen}
        hideDialogHandler={hideLocationDialogHandler}
        dialogTitle={dialogTitle}
        isFullScreenAllowed={true}
        maxWidth="lg"
        childComponent={
          locationDialogIsEdit
            ? <CreateUpdateLocation isEdit={true} closeDialog={hideLocationDialogHandler} />
            : <CreateUpdateLocation isEdit={false} closeDialog={hideLocationDialogHandler} onLocationCreated={handleLocationCreated} />
        }
      />
    </Box>
  );
}
export { getStatistics, getFilteredOrganizations, getFilteredLocations };
export default ClientDetailView;
